import type { IntentCandidate, IntentDefinition } from '../types/LocalAiTypes';
import { extractDeterministicParameters, normalizeTranscript } from '../normalize/TranscriptNormalizer';
import { contextBoost, contextMatches } from './ContextMatcher';

export function patternCandidates(
  normalized: string,
  intents: readonly IntentDefinition[],
  route: string,
): IntentCandidate[] {
  const matches: IntentCandidate[] = [];
  for (const intent of intents) {
    if (!contextMatches(intent, route)) continue;
    const phrases = [
      ...intent.examples.fa,
      ...intent.examples.en,
      ...intent.examples.ar,
      ...intent.aliases.fa,
      ...intent.aliases.en,
      ...intent.aliases.ar,
    ];
    let score = 0;
    for (const phrase of phrases) {
      const reference = normalizeTranscript(phrase).normalizedText;
      score = Math.max(score, phraseScore(normalized, reference));
    }
    score = Math.max(0, Math.min(1, score + contextBoost(intent, route)));
    if (score < 0.48) continue;
    const parameters = parametersFor(intent, normalized);
    if (intent.parameters.some((item) => item.required && parameters[item.name] === undefined)) score -= 0.12;
    matches.push({ intent, confidence: Math.max(0, score), source: 'pattern', parameters });
  }
  return matches.sort((left, right) => right.confidence - left.confidence);
}

function phraseScore(value: string, reference: string): number {
  if (value === reference) return 0.99;
  if (value.includes(reference)) {
    const specificity = reference.split(' ').length / Math.max(1, value.split(' ').length);
    return 0.86 + 0.1 * specificity;
  }
  if (reference.includes(value)) {
    const completeness = value.split(' ').length / Math.max(1, reference.split(' ').length);
    return 0.82 + 0.08 * completeness;
  }
  const valueTokens = new Set(value.split(' ').filter(Boolean));
  const referenceTokens = new Set(reference.split(' ').filter(Boolean));
  const intersection = [...referenceTokens].filter((token) => valueTokens.has(token)).length;
  if (intersection === 0) return 0;
  const precision = intersection / Math.max(1, valueTokens.size);
  const recall = intersection / Math.max(1, referenceTokens.size);
  return 0.42 + 0.5 * (2 * precision * recall / (precision + recall));
}

function parametersFor(intent: IntentDefinition, value: string): Record<string, unknown> {
  const global = extractDeterministicParameters(value);
  const extracted: Record<string, unknown> = { ...global, ...extractTextParameters(value) };
  return Object.fromEntries(intent.parameters
    .filter((parameter) => extracted[parameter.name] !== undefined)
    .map((parameter) => [parameter.name, extracted[parameter.name]]));
}

function extractTextParameters(value: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const captures: readonly [string, RegExp][] = [
    ['customerQuery', /(?:customer|مشتری|برای|عميل|زبون)\s+(?:شرکت|شركة)?\s*([^،,.]+?)(?=\s+(?:invoice|فاکتور|فاتورة|را|بگذار|بساز|باز|أنشئ|افتح)|$)/iu],
    ['supplierQuery', /(?:supplier|vendor|تامین کننده|فروشنده|مورد)\s+([^،,.]+?)(?=\s+(?:invoice|فاکتور|فاتورة|را|بگذار|بساز|باز|أنشئ|افتح)|$)/iu],
    ['warehouseQuery', /(?:warehouse|انبار|مستودع)\s+([^،,.]+?)(?=\s+(?:را|stock|موجودی|مخزون|انتخاب|باز|اختر|افتح)|$)/iu],
    ['productQuery', /(?:product|item|کالا|محصول|منتج|صنف)\s+([^،,.]+?)(?=\s+(?:را|اضافه|پیدا|باز|عدد|تا|items?|units?|أضف|ابحث|افتح|عناصر|وحدات)|$)/iu],
    ['accountQuery', /(?:account|حساب)\s+([^،,.]+?)(?=\s+(?:را|balance|مانده|دفتر|باز|پیدا|رصيد|افتح|ابحث)|$)/iu],
    ['notes', /(?:notes?|توضیحات|ملاحظات)\s+(?:را\s+)?(.+?)(?=\s+(?:بگذار|set|عيّن)$|$)/iu],
  ];
  for (const [name, expression] of captures) {
    const captured = expression.exec(value)?.[1]?.trim();
    if (captured) result[name] = captured.slice(0, 160);
  }
  const party = /(?:party|طرف حساب|طرف الحساب)\s+(?:to|را)?\s*([^،,.]+?)(?=\s+(?:بگذار|set|عيّن)$|$)/iu.exec(value)?.[1]?.trim();
  if (party) result.entityQuery = party.slice(0, 160);
  const row = /(?:row|ردیف|صف)\s+(\d+)/iu.exec(value)?.[1];
  if (row) result.rowNumber = Number(row);
  return result;
}
